import { useState, type ReactNode } from 'react'
import { Box, Flex, Text, VStack, Input, Textarea } from '@chakra-ui/react'
import {
  DataTable,
  DataList,
  PageHeader,
  Modal,
  ConfirmDialog,
  toaster,
} from '@minetrace/ui'
import { brand, themeColors } from '../theme'
import { usePagedRequest } from '../hooks'
import type { PagedResponse } from '../api/client'
import { usePermissions } from '../auth/PermissionContext'

// ─── Field config types ───
export type FieldType = 'text' | 'textarea' | 'number' | 'checkbox' | 'select'

export interface SelectOption {
  value: string | number
  label: string
}

export interface FieldConfig<T> {
  name: keyof T & string
  label: string
  type: FieldType
  required?: boolean
  placeholder?: string
  options?: SelectOption[]
  helpText?: string
  disabled?: boolean
  colSpan?: 1 | 2
  step?: number
  min?: number
  max?: number
}

// ─── Column config ───
export interface ColumnConfig<T> {
  header: string
  render: (item: T) => ReactNode
}

// ─── Service shape ───
export interface CatalogService<T, ID = string> {
  getAll: (params: Record<string, unknown>) => Promise<PagedResponse<T>>
  create?: (data: Record<string, unknown>) => Promise<T>
  update: (id: ID, data: Record<string, unknown>) => Promise<T>
  delete?: (id: ID) => Promise<void>
}

// ─── Props ───
export interface CatalogoBasePageProps<T, ID = string> {
  title: string
  subtitle: string
  entityName: string
  service: CatalogService<T, ID>
  columns: ColumnConfig<T>[]
  fields: FieldConfig<T>[]
  keyExtractor: (item: T) => string
  idExtractor: (item: T) => ID
  /** Lookup-only mode: no create/delete buttons */
  isLookupOnly?: boolean
  /** Default values for a new item (used when opening create modal) */
  defaultValues?: Partial<T>
  /** Extra filter params passed to getAll */
  filterParams?: Record<string, unknown>
  /** Callback to decide if an item should block edit/delete (e.g. system roles) */
  isItemLocked?: (item: T) => boolean
  lockReason?: string
  /** Search placeholder */
  searchPlaceholder?: string
  /** Extra toolbar content (e.g. filter selects) */
  toolbarExtra?: ReactNode
  /** Extra content to render above the DataList (e.g. conversion tool) */
  extraHeader?: ReactNode
  /** Modal size when opening create/edit form */
  modalSize?: 'sm' | 'md' | 'lg' | 'xl'
  /** Permission required to show the "Nuevo" button. Hidden if missing. */
  createPermission?: string
  /** Permission required to show the "Editar" action. Hidden if missing. */
  updatePermission?: string
  /** Permission required to show the "Eliminar" action. Hidden if missing. */
  deletePermission?: string
  /** Extra entries appended to the row's three-dots menu. Use this to add
   *  navigation actions like "Permisos", "Ver historial", etc. without
   *  duplicating the action affordance as a separate column. */
  extraRowActions?: (item: T) => Array<{ label: string; onClick: () => void; color?: string }>
}

type FieldValue = string | number | boolean | null | undefined

export function CatalogoBasePage<T extends Record<string, unknown>, ID = string>({
  title,
  subtitle,
  entityName,
  service,
  columns,
  fields,
  keyExtractor,
  idExtractor,
  isLookupOnly = false,
  defaultValues = {},
  filterParams = {},
  isItemLocked,
  lockReason = 'Este registro es del sistema y no puede modificarse.',
  searchPlaceholder = 'Buscar...',
  toolbarExtra,
  extraHeader,
  modalSize = 'md',
  createPermission,
  updatePermission,
  deletePermission,
  extraRowActions,
}: CatalogoBasePageProps<T, ID>) {
  const colors = themeColors
  const { hasPermission } = usePermissions()
  const canCreate = !createPermission || hasPermission(createPermission)
  const canUpdate = !updatePermission || hasPermission(updatePermission)
  const canDelete = !deletePermission || hasPermission(deletePermission)
  const [search, setSearch] = useState('')
  const [isModalOpen, setModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<T | null>(null)
  const [deletingItem, setDeletingItem] = useState<T | null>(null)
  const [formValues, setFormValues] = useState<Record<string, FieldValue>>({})
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})


  const {
    items,
    totalCount,
    isFetching,
    error,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
    refresh,
  } = usePagedRequest<T, Record<string, unknown>>(
    (params) => service.getAll(params),
    {
      params: { search: search.trim() || undefined, ...filterParams },
    },
  )

  const openCreate = () => {
    const initial: Record<string, FieldValue> = {}
    fields.forEach((f) => {
      const dv = (defaultValues as Record<string, FieldValue>)[f.name]
      if (dv !== undefined) initial[f.name] = dv
      else if (f.type === 'checkbox') initial[f.name] = false
      else if (f.type === 'number') initial[f.name] = 0
      else initial[f.name] = ''
    })
    setFormValues(initial)
    setEditingItem(null)
    setErrors({})
    setModalOpen(true)
  }

  const openEdit = (item: T) => {
    const initial: Record<string, FieldValue> = {}
    fields.forEach((f) => {
      const raw = item[f.name]
      if (raw === null || raw === undefined) {
        if (f.type === 'checkbox') initial[f.name] = false
        else if (f.type === 'number') initial[f.name] = 0
        else initial[f.name] = ''
      } else {
        initial[f.name] = raw as FieldValue
      }
    })
    setFormValues(initial)
    setEditingItem(item)
    setErrors({})
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingItem(null)
    setFormValues({})
    setErrors({})
  }

  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    fields.forEach((f) => {
      if (f.required) {
        const v = formValues[f.name]
        if (v === '' || v === null || v === undefined) {
          errs[f.name] = 'Campo requerido'
        }
      }
    })
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {}
      fields.forEach((f) => {
        let v = formValues[f.name]
        if (f.type === 'number' && typeof v === 'string') v = v === '' ? null : Number(v)
        if (f.type === 'select' && typeof v === 'string' && f.options) {
          const opt = f.options.find((o) => String(o.value) === v)
          if (opt) v = opt.value
        }
        payload[f.name] = v
      })

      if (editingItem) {
        await service.update(idExtractor(editingItem), payload)
        toaster.success({ description: `${entityName} actualizado correctamente` })
      } else if (service.create) {
        await service.create(payload)
        toaster.success({ description: `${entityName} creado correctamente` })
      }
      refresh()
      closeModal()
    } catch {
      // Error toast already shown by apiClient
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem || !service.delete) return
    setDeleting(true)
    try {
      await service.delete(idExtractor(deletingItem))
      toaster.success({ description: `${entityName} eliminado correctamente` })
      setDeletingItem(null)
      refresh()
    } catch {
      // Error toast already shown
    } finally {
      setDeleting(false)
    }
  }

  const setField = (name: string, value: FieldValue) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[name]
        return next
      })
    }
  }

  const renderField = (f: FieldConfig<T>) => {
    const value = formValues[f.name]
    const err = errors[f.name]
    const labelNode = (
      <Flex gap={1} alignItems="center" mb={1}>
        <Text fontSize="xs" fontWeight="600" color={colors.textSecondary}>
          {f.label}
        </Text>
        {f.required && <Text fontSize="xs" color="#b23a26">*</Text>}
      </Flex>
    )

    let inputNode: ReactNode = null
    switch (f.type) {
      case 'textarea':
        inputNode = (
          <Textarea
            value={(value as string) ?? ''}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder}
            disabled={f.disabled}
            rows={3}
            fontSize="sm"
          />
        )
        break
      case 'number':
        inputNode = (
          <Input
            type="number"
            value={(value as number | string | undefined) ?? ''}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder}
            disabled={f.disabled}
            step={f.step}
            min={f.min}
            max={f.max}
            fontSize="sm"
          />
        )
        break
      case 'checkbox':
        inputNode = (
          <Flex alignItems="center" gap={2} mt={1}>
            <Box
              as="input"
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField(f.name, e.target.checked)
              }
              disabled={f.disabled}
              width="16px"
              height="16px"
              accentColor={brand.primary}
              cursor={f.disabled ? 'not-allowed' : 'pointer'}
            />
            {f.helpText && (
              <Text fontSize="xs" color={colors.textMuted}>
                {f.helpText}
              </Text>
            )}
          </Flex>
        )
        break
      case 'select':
        inputNode = (
          <Box
            as="select"
            value={String(value ?? '')}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setField(f.name, e.target.value)
            }
            disabled={f.disabled}
            width="100%"
            px={3}
            py={2}
            borderRadius="md"
            border="1px solid"
            borderColor={err ? '#b23a26' : colors.border}
            bg={colors.bgCard}
            fontSize="sm"
            color={colors.text}
            cursor={f.disabled ? 'not-allowed' : 'pointer'}
          >
            <option value="">{f.placeholder ?? 'Seleccione...'}</option>
            {f.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Box>
        )
        break
      default:
        inputNode = (
          <Input
            value={(value as string | undefined) ?? ''}
            onChange={(e) => setField(f.name, e.target.value)}
            placeholder={f.placeholder}
            disabled={f.disabled}
            fontSize="sm"
          />
        )
    }

    return (
      <Box key={f.name} gridColumn={f.colSpan === 2 ? 'span 2' : undefined}>
        {f.type !== 'checkbox' && labelNode}
        {f.type === 'checkbox' && (
          <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={1}>
            {f.label}
          </Text>
        )}
        {inputNode}
        {err && (
          <Text fontSize="xs" color="#b23a26" mt={1}>
            {err}
          </Text>
        )}
        {f.helpText && f.type !== 'checkbox' && (
          <Text fontSize="xs" color={colors.textMuted} mt={1}>
            {f.helpText}
          </Text>
        )}
      </Box>
    )
  }

  return (
    <VStack gap={5} alignItems="stretch">
      <PageHeader
        title={title}
        subtitle={subtitle}
        colors={colors}
        buttonLabel={isLookupOnly || !service.create || !canCreate ? undefined : `Nuevo ${entityName}`}
        onButtonClick={isLookupOnly || !service.create || !canCreate ? undefined : openCreate}
      />

      {extraHeader}

      <DataList<T>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={searchPlaceholder}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName={entityName}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        brandColor={brand.primary}
        toolbarExtra={toolbarExtra}
        headers={columns.map((c) => c.header)}
        data={items}
        loading={isFetching}
        error={error}
        emptyMessage={`No se encontraron ${entityName}`}
        loadingMessage={`Cargando ${entityName}...`}
        colors={colors}
        keyExtractor={keyExtractor}
        renderRow={(item) => (
          <>
            {columns.map((col, i) => (
              <DataTable.Cell key={i}>{col.render(item)}</DataTable.Cell>
            ))}
          </>
        )}
        actions={(item) => {
          const locked = isItemLocked?.(item) ?? false
          const acts: { label: string; onClick: () => void; color?: string }[] = []
          if (canUpdate) {
            acts.push({
              label: 'Editar',
              onClick: () => {
                if (locked) {
                  toaster.error({ description: lockReason })
                  return
                }
                openEdit(item)
              },
              color: locked ? '#b5aa9d' : undefined,
            })
          }
          if (!isLookupOnly && service.delete && canDelete) {
            acts.push({
              label: 'Eliminar',
              onClick: () => {
                if (locked) {
                  toaster.error({ description: lockReason })
                  return
                }
                setDeletingItem(item)
              },
              color: locked ? '#b5aa9d' : '#c8442f',
            })
          }
          // Caller-provided actions go last so they don't shadow the
          // standard Edit/Delete affordances admins expect to find first.
          if (extraRowActions) {
            acts.push(...extraRowActions(item))
          }
          return acts
        }}
      />

      {/* Create / Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title={editingItem ? `Editar ${entityName}` : `Nuevo ${entityName}`}
        size={modalSize}
        footer={
          <>
            <Box
              as="button"
              type="button"
              px={5}
              py={2.5}
              borderRadius="md"
              fontSize="sm"
              fontWeight="600"
              color={colors.textSecondary}
              bg={colors.bgHover}
              cursor="pointer"
              _hover={{ bg: colors.bgCard }}
              onClick={closeModal}
            >
              Cancelar
            </Box>
            <Box
              as="button"
              type="button"
              px={5}
              py={2.5}
              borderRadius="md"
              fontSize="sm"
              fontWeight="600"
              color="white"
              bg={brand.primary}
              cursor={saving ? 'not-allowed' : 'pointer'}
              opacity={saving ? 0.7 : 1}
              _hover={{ opacity: 0.9 }}
              onClick={handleSave}
              aria-disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Box>
          </>
        }
      >
        <Box display="grid" gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
          {fields.map(renderField)}
        </Box>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deletingItem !== null}
        onClose={() => setDeletingItem(null)}
        onConfirm={handleDelete}
        title={`Eliminar ${entityName}`}
        variant="danger"
        confirmLabel={deleting ? 'Eliminando...' : 'Eliminar'}
        isLoading={deleting}
      >
        ¿Está seguro que desea eliminar este registro? Esta acción no se puede deshacer.
      </ConfirmDialog>
    </VStack>
  )
}
